import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import debounce from "lodash.debounce";
import { useSearchParams } from "react-router";
import { fetchSuppliers, fetchSuppliersByBenefitNetwork, fetchSuppliersBySupplierNetwork } from "../../utils/apiUtils";
import SharedTable from "../benefitPlan/SharedTable";
import Select from 'react-select';
import { camelCaseToWords } from "../../utils/commonUtils";

const limit = 20;
const searchOptions = [
    { value: 'supplierHccId', label: 'Supplier HccId' },
    { value: 'searchText', label: 'Supplier Name' },
    { value: 'taxId', label: 'Tax ID' },
    { value: 'supplierNpi', label: 'Supplier NPI' },
    { value: 'supplierKey', label: 'Supplier Key' },
]

const NETWORK_SEARCH = {
    supplierHccId: "supplierHccId",
    searchText: "name",
    taxId: "tin",
    supplierNpi: "npi",
};

const supplierHccOption = searchOptions[0];

const withQuery = (path, params) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v != null && v !== "") qs.set(k, String(v));
    });
    const s = qs.toString();
    return s ? `${path}?${s}` : path;
};

const mapNetworkSupplier = (row) => ({
    supplierHccId: row.hccId,
    supplierName: row.name,
    supplierNpi: row.npi,
    taxId: row.tin,
    taxName: row.taxName,
    supplierStatus: row.status,
    city: row.address?.city,
    state: row.address?.state,
    zip: row.address?.zip,
});

const Suppliers = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const benefitNetworkId = searchParams.get("benefitNetworkId") || "";
    const supplierNetworkHccId = searchParams.get("supplierNetworkHccId") || "";
    const networkName = searchParams.get("networkName") || "";
    const networkFilter = benefitNetworkId || supplierNetworkHccId;
    const urlSupplierHccId = searchParams.get("supplierHccId") || "";

    const [searchValue, setSearchValue] = useState(urlSupplierHccId || undefined);
    const [decouncedSearchValue, setDecouncedSearchValue] = useState(urlSupplierHccId || undefined);

    const [searchBy, setSearchBy] = useState(urlSupplierHccId ? supplierHccOption : null);

    const [showMoreColumns, setShowMoreColumns] = useState(false);

    const { ref, inView } = useInView();

    const { data, error, fetchNextPage, hasNextPage, isLoading, isPending, isFetching, isFetchingNextPage, status, refetch } = useInfiniteQuery({
        queryKey: ["supplier", limit, decouncedSearchValue, searchBy?.value, benefitNetworkId, supplierNetworkHccId, urlSupplierHccId],
        queryFn: async ({ pageParam }) => {
            if (networkFilter) {
                const page = typeof pageParam === "number" && pageParam > 0 ? pageParam : 1;
                const extra = {};
                const mapped = NETWORK_SEARCH[searchBy?.value];
                if (decouncedSearchValue && mapped) extra[mapped] = decouncedSearchValue;
                const result = benefitNetworkId
                    ? await fetchSuppliersByBenefitNetwork({ benefitNetworkId, page, pageSize: limit, ...extra })
                    : await fetchSuppliersBySupplierNetwork({ supplierNetworkHccId, page, pageSize: limit, ...extra });
                return {
                    count: Number(result?.meta?.total) || 0,
                    rows: (result?.data || []).map(mapNetworkSupplier),
                };
            }

            let params = pageParam || {};

            if (decouncedSearchValue && searchBy?.value) {
                params[searchBy?.value] = decouncedSearchValue;
            }

            return await fetchSuppliers({ ...params, limit });
        },

        select: (data) => {
            return { count: data.pages?.[0]?.count, rows: data.pages.map((page) => page.rows).flatMap((x) => x) };
        },
        initialPageParam: networkFilter ? 1 : 0,
        getNextPageParam: (lastPage, pages) => {
            if (!lastPage?.rows?.length || lastPage.rows.length < limit) return undefined;
            if (networkFilter) {
                const loaded = pages.reduce((accum, page) => accum + (page.rows?.length || 0), 0);
                const total = Number(lastPage.count) || 0;
                if (total && loaded >= total) return undefined;
                return pages.length + 1;
            }
            return {
                offset: pages.reduce((accum, page) => accum + page.rows.length, 0),
            };
        },
        staleTime: 0,
        enabled: true
    });

    useEffect(() => {
        if (inView) {
            fetchNextPage();
        }
    }, [inView]);

    const debouncedChangeHandler = useCallback(
        debounce((event) => {
            setDecouncedSearchValue(event.target.value?.trim());
        }, 500),
        []
    );

    let columns = networkFilter
        ? ["supplierHccId", "supplierName", "supplierNpi", "taxId", "taxName", "supplierStatus", "city", "state"]
        : ['supplierHccId', 'supplierName', 'supplierNpi', 'taxId', 'taxName', 'isPcp', 'excludeFromProviderDir', 'supplierCorrAddressKey', 'taxAddressKey', "versionEffDateKey",
            "versionExpDateKey"];

    if (showMoreColumns && !networkFilter) {
        columns = [
            'supplierKey',
            ...columns,
            'supplierEffectiveDateKey',
            'supplierTerminationDateKey',
        ];
    }

    columns = [...columns, "contracts"];

    const onDetailHandler = async (body) => {


        return {}
    }

    const toRoles = (row) => row.supplierHccId
        ? withQuery("/providers/roles", {
            supplierHccId: row.supplierHccId,
            benefitNetworkId,
            supplierNetworkHccId,
            networkName,
        })
        : null;

    const toContracts = (row) => row.supplierHccId
        ? withQuery("/providers/contracts", {
            supplierHccId: row.supplierHccId,
            supplierName: row.supplierName,
        })
        : null;

    const columnLinks = {
        supplierHccId: toRoles,
        contracts: toContracts,
    };

    return (
        <div className={'container '}>
            <p className='title'>Suppliers ( {data?.count || 0} )</p>
            <p className="sub-title">Suppliers from Health rules</p>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1rem 1rem 0rem', }}>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', width: '100%', flexWrap: 'wrap', }}>
                    {networkFilter && (
                        <Select
                            name="network"
                            className="react-select-container large-input"
                            classNamePrefix="react-select"
                            placeholder="Network"
                            isClearable
                            value={{
                                value: networkFilter,
                                label: `${benefitNetworkId ? "Benefit network" : "Supplier network"}: ${networkName || networkFilter}`,
                            }}
                            options={[{
                                value: networkFilter,
                                label: `${benefitNetworkId ? "Benefit network" : "Supplier network"}: ${networkName || networkFilter}`,
                            }]}
                            onChange={(option) => {
                                if (!option) {
                                    const next = new URLSearchParams(searchParams);
                                    next.delete("benefitNetworkId");
                                    next.delete("supplierNetworkHccId");
                                    next.delete("networkName");
                                    setSearchParams(next);
                                }
                            }}
                        />
                    )}
                    <Select
                        name={'searchBy'}
                        className="react-select-container large-input"
                        classNamePrefix="react-select"
                        placeholder={'Search by'}
                        // inputRef={field.ref}
                        options={searchOptions}
                        value={searchBy} // Pass the state variable here
                        onChange={(option) => { setSearchBy(option) }}
                    // value={options.find((c) => c.value === field.value)}
                    // onChange={(val) => field.onChange(val.value)}
                    />

                    {searchBy && <input
                        style={{ fontSize: "1rem", minWidth: "250px", padding: "1rem", borderRadius: ".4rem", border: "1px solid var(--lighterGrey)", outline: "none" }}
                        type="text"
                        id={"search"}
                        placeholder="search..."
                        // list="items"
                        autoComplete="off"
                        // onFocus={() => setCurrentPage(0)}
                        onChange={(e) => {
                            setSearchValue(e.target.value);
                            debouncedChangeHandler(e);
                        }}
                        value={searchValue}
                    />}

                </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0rem 1rem 1rem 0rem" }}>
                <p
                    style={{ cursor: "pointer", color: "var(--red)" }}
                    onClick={() => {
                        setSearchBy(null)
                        setDecouncedSearchValue("");
                        setSearchValue("");
                        if (networkFilter || urlSupplierHccId) setSearchParams({});
                    }}
                >
                    Clear filters
                </p>

                <p style={{ cursor: "pointer", color: "var(--green)" }} onClick={() => setShowMoreColumns(!showMoreColumns)}>
                    {showMoreColumns ? "Less Columns" : "More Columns"}
                </p>

            </div>


            {status === "loading" ? (
                <p>Loading...</p>
            ) : status === "error" ? (
                <span className='danger'>Error: {error.message}</span>
            ) : (
                <>
                    <SharedTable
                        type={"suppliers"}
                        columns={columns}
                        items={(data?.rows || []).map((row) => ({ ...row, contracts: "Contracts" }))}
                        activeDraftId={null}
                        hasNextPage={hasNextPage}
                        isFetchingNextPage={isFetchingNextPage}
                        fetchNextPage={fetchNextPage}
                        isFetching={isFetching}
                        isLoading={isLoading}
                        // onEditHandler={onEditHandler}
                        onDetailHandler={onDetailHandler}
                        columnLinks={columnLinks}
                        ref={ref}
                    />
                </>
            )}

        </div>
    )
}

export default Suppliers